import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { PictureAsPdf, Refresh } from '@mui/icons-material';
import { Facture, TypeFacture } from '../types';
import { formatEuro, formatDate } from '../utils/calculations';
import avoirService from '../services/avoirService';
import { useNotification } from '../contexts/NotificationContext';

interface AvoirsListProps {
  factureId: number;
  onRefresh?: () => void;
}

const AvoirsList: React.FC<AvoirsListProps> = ({ factureId, onRefresh }) => {
  const { showError } = useNotification();
  const [avoirs, setAvoirs] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const loadAvoirs = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await avoirService.getAvoirsByFactureId(factureId);
      setAvoirs(data);
    } catch (err: any) {
      console.error('Erreur lors du chargement des avoirs:', err);
      setError('Impossible de charger les avoirs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvoirs();
  }, [factureId]);

  const handleDownloadPDF = async (avoirId: number, avoirNumero: string) => {
    try {
      const blob = await avoirService.downloadPDF(avoirId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Avoir_${avoirNumero}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Erreur lors du téléchargement du PDF:', err);
      showError('Erreur lors du téléchargement du PDF');
    }
  };

  const handleRefresh = () => {
    loadAvoirs();
    if (onRefresh) {
      onRefresh();
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (avoirs.length === 0) {
    return null; // Ne rien afficher s'il n'y a pas d'avoirs
  }

  // Calculer le total des avoirs
  const totalAvoirs = avoirs.reduce((sum, avoir) => sum + Math.abs(avoir.montantTTC), 0);

  return (
    <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
          Avoirs associés
        </Typography>
        <Tooltip title="Actualiser la liste">
          <IconButton onClick={handleRefresh} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Numéro</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date d'émission</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Montant HT</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Montant TTC</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {avoirs.map((avoir) => (
              <TableRow key={avoir.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {avoir.numero}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(avoir.dateEmission)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label="AVOIR"
                    size="small"
                    color="error"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {formatEuro(avoir.montantHT)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {formatEuro(avoir.montantTTC)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Télécharger le PDF">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleDownloadPDF(avoir.id, avoir.numero)}
                    >
                      <PictureAsPdf />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Total des avoirs */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: '2px solid #e0e0e0' }}>
        <Box sx={{ width: { xs: '100%', sm: '300px' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Total des avoirs TTC:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.main' }}>
              - {formatEuro(totalAvoirs)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default AvoirsList;
